import { SxProps, Theme, styled } from "@mui/material/styles";
import {
    Box,
    Checkbox,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer as MuiTableContainer,
    TablePagination,
    TableRow,
    List,
    Typography,
    LinearProgress,
} from "@mui/material";
import {
    ReactNode,
    useState,
    MouseEvent,
    ChangeEvent,
    useMemo,
    useEffect,
    useRef,
} from "react";
import filterFunction, { EFilterType } from "./filter/filter";
import DataFilter, { IFilter } from "./filter/DataFilter";
import Header, { Order } from "./header/Header";
import { useTranslation } from "react-i18next";
import RowContextMenu from "./RowContextMenu";

const TableContainer = styled(Paper)(() => ({
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
}));

function acendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    const itemA = a[orderBy];
    const itemB = b[orderBy];

    if (!itemA) return 1;
    if (!itemB) return -1;

    if (typeof itemA === "string") {
        return (itemA as string).toLowerCase() > (itemB as string).toLowerCase() ? 1 : -1;
    }

    return itemA > itemB ? 1 : -1;
}

function getComparator<T, Key extends keyof T>(
    order: Order,
    orderBy?: Key
): (a: T, b: T) => number {
    if (!orderBy) return () => 0;

    return order === "desc"
        ? (a, b) => -acendingComparator(a, b, orderBy)
        : (a, b) => acendingComparator(a, b, orderBy);
}

type EntityWithId = {
    id: number | string;
};

export type TData<T> = T & EntityWithId;
type PrimitiveType = string | number | Date;

export interface IColumnTransform<T> {
    render: (data: TData<T>) => ReactNode;
    filterTransform: (
        data: TData<T>
    ) => PrimitiveType | PrimitiveType[] | undefined;
}

export interface IColumn<T> {
    field: keyof T;
    headerName: string;
    renderHeader?: ReactNode;
    align?: "right" | "left" | "center" | "inherit" | "justify";
    disablePadding?: boolean;
    truncate?: { maxWidth: string };
    transform?: IColumnTransform<T>;
    hidden?: boolean;

}

export const getValueToFilter = <T,>(
    row: TData<T>,
    field: keyof TData<T>,
    column: IColumn<TData<T>> | undefined
) =>
    (column && column.transform && column.transform.filterTransform(row)) ||
    row[field];

interface DataTableProps<T> {
    rows: TData<T>[];
    columns: IColumn<TData<T>>[];
    dense?: boolean;
    disableFiltrer?: boolean;
    checkable?: boolean;
    isLoading?: boolean;
    style?: SxProps<Theme>
    onRowClick?: (row: TData<T>) => void;
    getRowNavTo?: (row?: TData<T>) => string;
    rowsPerPageOptions?: number[];
    onSetFilter?: (filteredRows: TData<T>[], filters: Map<string, IFilter>) => void;
    initialFilters?: Map<string, IFilter>;
    hasBorder?: boolean;
    onSelectionChange?: (selectedIds: (number | string)[]) => void;
    selectedIds?: (number | string)[];
    height?: string

}
const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];
const DataTable = <T extends Object>({
    rows,
    columns,
    dense,
    disableFiltrer,
    checkable,
    isLoading = false,
    style,
    onRowClick,
    getRowNavTo,
    rowsPerPageOptions,
    onSetFilter,
    initialFilters,
    hasBorder = true,
    onSelectionChange,
    selectedIds = [],
    height

}: DataTableProps<T>) => {
    const { t } = useTranslation();
    const history = window.history

    const [order, setOrder] = useState<Order>("asc");
    const [orderBy, setOrderBy] = useState<keyof TData<T>>();
    const selected = selectedIds;

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(
        rowsPerPageOptions ? rowsPerPageOptions[0] : ROWS_PER_PAGE_OPTIONS[0]
    );
    const [filteredRows, setFilteredRows] = useState(rows);
    const [selectedFilters, setSelectedFilters] = useState<
        Map<string, IFilter>
    >(history.state.filters ? history.state.filters : initialFilters ? initialFilters : new Map());

    const [isMenuVisible, setIsMenuVisible] = useState<boolean>(false);
    const [menuPosition, setMenuPosition] = useState<{ x: number, y: number }>({ x: 0, y: 0 })
    const [selectedRow, setSelectedRow] = useState<TData<T>>();

    const onRequestSort = (
        _: MouseEvent<unknown>,
        property: keyof TData<T>
    ) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };


    const onSelect = (event: MouseEvent<unknown>, id: number | string) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected: (number | string)[] = [...selected];

        if (selectedIndex === -1) {
            newSelected.push(id);
        } else {
            newSelected.splice(selectedIndex, 1);
        }
        onSelectionChange?.(newSelected);
    };


    const onSelectAllClick = (event: ChangeEvent<HTMLInputElement>) => {
        const allFilteredIds = filteredRows.map((n) => n.id);
        let newSelected: (number | string)[] = [...selected];

        if (event.target.checked) {
            const newSelectedSet = new Set([...newSelected, ...allFilteredIds]);
            newSelected = Array.from(newSelectedSet);
        } else {
            newSelected = newSelected.filter(id => !allFilteredIds.includes(id));
        }
        onSelectionChange?.(newSelected);
    };




    const onPageChange = (_: unknown, newPage: number) => {
        setPage(newPage);
    };

    const onRowsPerPageChange = (event: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const isSelected = (id: number | string) => selected.indexOf(id) !== -1;

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

    const visibleRows: TData<T>[] = useMemo(
        () =>
            filteredRows
                .sort(getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [order, orderBy, page, rowsPerPage, filteredRows]
    );

    useEffect(() => {
        const filters: IFilter[] = [];

        selectedFilters.forEach((filter, field) => {
            if (!filter.isActive || !filter.request) return;
            filters.push(filter);
        });

        if (!filters.length) {
            setFilteredRows(rows);
            return;
        }

        let newRows: TData<T>[] = rows;
        filters.forEach((filter) => {
            const column = columns.find((c) => c.field === filter.field);

            newRows = newRows.filter((row) =>
                filterFunction.filter(
                    getValueToFilter(
                        row,
                        filter.field as keyof TData<T>,
                        column
                    ),
                    filter.type,
                    filter.request,
                    filter.isCaseSensitive
                )
            );
        });

        setFilteredRows(newRows);
    }, [rows, selectedFilters]);

    useEffect(() => {
        if (onSetFilter) onSetFilter(filteredRows, selectedFilters)
    }, [filteredRows])



    const numSelectedInFiltered = useMemo(() => {
        const filteredIds = new Set(filteredRows.map(row => row.id));
        return selected.filter(id => filteredIds.has(id)).length;
    }, [selected, filteredRows]);


    return (<>
        <div style={{ height: height ?? "600px" }}>

            <Box display="flex" sx={{
                ...style,
                height: '100%', // Parent container takes available height
            }}>
                {getRowNavTo && <RowContextMenu rowNavTo={getRowNavTo(selectedRow)} isVisible={isMenuVisible} setIsVisible={setIsMenuVisible} position={menuPosition} row={selectedRow} />}
                {!disableFiltrer && (
                    <Box
                        component={Paper}
                        sx={{
                            marginRight: 1,
                            minWidth: "155px",
                            maxWidth: "200px",
                            maxHeight: "100vh",
                            overflowY: "auto",

                        }}
                    >
                        <List>
                            {rows.length &&
                                columns
                                    .filter(col => !col.hidden)
                                    .map((column, index) => {
                                        const initialFilterColumn = selectedFilters && selectedFilters.get(column.field as string)
                                        return (
                                            <DataFilter
                                                key={index}
                                                filter={initialFilterColumn ? {
                                                    field: initialFilterColumn.field,
                                                    isActive: initialFilterColumn.isActive,
                                                    request: initialFilterColumn.request,
                                                    type: initialFilterColumn.type,
                                                    isCaseSensitive: initialFilterColumn.isCaseSensitive
                                                } : {
                                                    field: column.field as string,
                                                    isActive: false,
                                                    request: "",
                                                    type: EFilterType.Is,
                                                }}
                                                OnFilter={(filter) => {
                                                    setSelectedFilters((old) => {
                                                        const newFilters = new Map(
                                                            old.set(
                                                                filter.field,
                                                                filter
                                                            )
                                                        );
                                                        history.replaceState({ filters: newFilters }, "")
                                                        return newFilters;
                                                    });
                                                }}
                                                rows={rows}
                                                column={column}
                                                filteredRows={filteredRows}
                                            />
                                        );
                                    })}
                        </List>
                    </Box>
                )}
                <TableContainer sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden', // Prevent container from scrolling
                    boxShadow: hasBorder ? "auto" : "none",
                }}>
                    {isLoading ? (
                        <Box display="flex" justifyContent="center" alignItems="flex-start" width="100%" height="100%" paddingTop='7vh'>
                            <LinearProgress sx={{ width: '50vh' }} />
                        </Box>
                    ) : (
                        <MuiTableContainer sx={{ flex: 1 }}>
                            <Table
                                stickyHeader
                                aria-labelledby="tableTitle"
                                size={dense ? "small" : "medium"}
                            >

                                <Header
                                    numSelected={numSelectedInFiltered}
                                    order={order}
                                    orderBy={orderBy as string}
                                    onSelectAllClick={onSelectAllClick}
                                    onRequestSort={onRequestSort}
                                    rowCount={filteredRows.length}
                                    columns={columns.filter(col => !col.hidden)}
                                    checkable={checkable || false}
                                />
                                <TableBody sx={{ height: "100%", overflow: "scroll" }}>
                                    {visibleRows.map((row, index) => {
                                        const isItemSelected = isSelected(row.id);
                                        const labelId = `enhanced-table-checkbox-${index}`;

                                        return (
                                            <TableRow
                                                hover
                                                onClick={(event) => {
                                                    if (checkable) {
                                                        onSelect(event, row.id);
                                                    } else {
                                                        onRowClick && onRowClick(row);
                                                    }
                                                }}
                                                onContextMenu={(event) => {
                                                    event.preventDefault()
                                                    setIsMenuVisible(true)
                                                    setMenuPosition({ x: event.pageX, y: event.pageY })
                                                    setSelectedRow(row)
                                                }}
                                                role={checkable ? "checkbox" : "row"}
                                                aria-checked={isItemSelected}
                                                tabIndex={-1}
                                                key={row.id}
                                                selected={isItemSelected}
                                                sx={{ cursor: "pointer" }}
                                            >
                                                {checkable && (
                                                    <TableCell padding="checkbox">
                                                        <Checkbox
                                                            color="primary"
                                                            checked={isItemSelected}
                                                            inputProps={{
                                                                "aria-labelledby":
                                                                    labelId,
                                                            }}
                                                        />
                                                    </TableCell>
                                                )}
                                                {
                                                    columns
                                                        .filter(col => !col.hidden)
                                                        .map(
                                                            ({
                                                                align,
                                                                field,
                                                                truncate,
                                                                transform,
                                                            }, index) => (
                                                                <TableCell
                                                                    sx={{
                                                                        maxWidth:
                                                                            truncate?.maxWidth,
                                                                    }}
                                                                    align={align}
                                                                    key={index}
                                                                >
                                                                    <>
                                                                        {(transform &&
                                                                            transform.render(
                                                                                row
                                                                            )) || (
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    noWrap={
                                                                                        (truncate &&
                                                                                            true) ||
                                                                                        false
                                                                                    }
                                                                                >
                                                                                    {
                                                                                        row[
                                                                                        field as keyof TData<T>
                                                                                        ] as string
                                                                                    }
                                                                                </Typography>
                                                                            )}
                                                                    </>
                                                                </TableCell>
                                                            )
                                                        )}

                                            </TableRow>
                                        );
                                    })}
                                    {emptyRows > 0 && (
                                        <TableRow
                                            style={{
                                                height: (dense ? 33 : 53) * emptyRows,
                                            }}
                                        >
                                            <TableCell colSpan={6} />
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </MuiTableContainer>
                    )}
                    <TablePagination
                        rowsPerPageOptions={
                            rowsPerPageOptions || ROWS_PER_PAGE_OPTIONS
                        }
                        component={Paper}
                        count={filteredRows.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={onPageChange}
                        onRowsPerPageChange={onRowsPerPageChange}
                        sx={{ overflowY: "auto", boxShadow: "none" }}
                        labelDisplayedRows={({ from, to, count }) =>
                            `${from}-${to} ${t("dataTable.of")} ${count}`
                        }
                        labelRowsPerPage={t("dataTable.row")}
                    />
                </TableContainer>
            </Box>
        </div>
    </>);
};

export default DataTable;